/**
 * ClaimStatusPoller — W1.7
 *
 * Status-Anzeige mit Polling-Loop.
 * AC-5: pending / submitted / confirmed / failed
 * AC-6: Erfolgs-Pill + Solscan-Link (neuer Tab)
 * AC-7: Failed-Pill + failure_reason + Retry + Support-Link
 * AC-8: Timeout-State mit Claim-ID und manuellem Retry
 */
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useClaimPoller } from './hooks/useClaimPoller'

type Props = {
  claimId: string
  onConfirmed: (txSignature: string) => void
  onFailed: (reason: string) => void
  onTimeout: () => void
  onRetry: () => void
  onRefetchBalance: () => void
}

function truncateSig(sig: string): string {
  if (sig.length <= 16) return sig
  return `${sig.slice(0, 8)}...${sig.slice(-8)}`
}

export function ClaimStatusPoller({
  claimId,
  onConfirmed,
  onFailed,
  onTimeout,
  onRetry,
  onRefetchBalance,
}: Props) {
  const { t } = useTranslation('claims')
  const { status, txSignature, failureReason, timedOut, attempts, resetPoller } =
    useClaimPoller(claimId)

  // Callbacks bei Terminal-State
  useEffect(() => {
    if (status === 'confirmed' && txSignature) {
      onRefetchBalance()
      onConfirmed(txSignature)
    }
  }, [status, txSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === 'failed') {
      onFailed(failureReason ?? t('statusPoller.unknownError'))
    }
  }, [status, failureReason]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (timedOut) {
      onTimeout()
    }
  }, [timedOut]) // eslint-disable-line react-hooks/exhaustive-deps

  if (timedOut) {
    return (
      <div className="claim-status-timeout" role="status">
        <p>{t('statusPoller.timeout.message')}</p>
        <p>
          <strong>{t('statusPoller.timeout.claimIdLabel')}</strong>{' '}
          <button
            type="button"
            className="copy-btn"
            onClick={() => navigator.clipboard.writeText(claimId)}
            aria-label={t('statusPoller.timeout.copyAriaLabel', { claimId })}
          >
            {claimId}
          </button>
        </p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            resetPoller()
          }}
        >
          {t('statusPoller.timeout.checkStatus')}
        </button>
      </div>
    )
  }

  if (status === 'confirmed' && txSignature) {
    const solscanUrl = `https://solscan.io/tx/${txSignature}`
    return (
      <div className="claim-status-success" role="status">
        <span className="pill-success">{t('statusPoller.success.pill')}</span>
        <p>
          Tx:{' '}
          <a href={solscanUrl} target="_blank" rel="noopener noreferrer">
            {truncateSig(txSignature)}
          </a>
        </p>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div className="claim-status-failed" role="status">
        <span className="pill-danger">{t('statusPoller.failed.pill')}</span>
        {failureReason && <p>{failureReason}</p>}
        <div className="failed-actions">
          <button type="button" className="btn btn-secondary" onClick={onRetry}>
            {t('common:actions.retry')}
          </button>
          <a
            href="https://discord.gg/heliumtracker"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost"
          >
            {t('statusPoller.failed.support')}
          </a>
        </div>
      </div>
    )
  }

  const statusText: Record<string, string> = {
    pending: t('statusPoller.polling.pending'),
    submitted: t('statusPoller.polling.submitted'),
  }

  return (
    <div className="claim-status-polling" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p>{status ? (statusText[status] ?? t('statusPoller.polling.processing')) : t('statusPoller.polling.initializing')}</p>
      <p className="muted" aria-label={t('statusPoller.polling.attemptsAriaLabel', { attempts })}>
        ({attempts} / 40)
      </p>
    </div>
  )
}
